import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private meta = inject(Meta);
  private title = inject(Title);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private routeSub!: Subscription;

  private readonly baseDomain = 'https://prinxcr.com';

  init(): void {
    this.updateCanonical();

    this.routeSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCanonical();
      });
  }

  destroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  private updateCanonical(): void {
    const canonicalUrl = this.buildCanonicalUrl();

    if (isPlatformBrowser(this.platformId)) {
      this.updateCanonicalInBrowser(canonicalUrl);
    } else if (isPlatformServer(this.platformId)) {
      this.updateCanonicalOnServer(canonicalUrl);
    }
  }

  private buildCanonicalUrl(): string {
    const routePath = this.getRoutePath(this.activatedRoute);
    return routePath ? `${this.baseDomain}/${routePath}` : this.baseDomain;
  }

  private updateCanonicalInBrowser(canonicalUrl: string): void {
    const existingCanonical = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalUrl);
    } else {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(link);
    }
  }

  private updateCanonicalOnServer(canonicalUrl: string): void {
    const existingCanonical = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalUrl);
    } else {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(link);
    }
  }

  private getRoutePath(route: ActivatedRoute): string {
    const segments: string[] = [];
    let currentRoute: ActivatedRoute | null = route;

    while (currentRoute) {
      const routeUrl = currentRoute.snapshot.url.map((segment) => segment.path).join('/');
      if (routeUrl) {
        segments.push(routeUrl);
      }
      currentRoute = currentRoute.firstChild;
    }

    return segments.join('/').replace(/\/$/, '');
  }
}
